import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  Input,
  OnDestroy,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import { ProgressBarColor } from '@epicuro-next/ataraxia/progress-bar';
import { inventoryByExternalIdLocale } from '@epicuro-next/constants/features/localization';
import {
  ConfigStripConfigurationKeys,
  ConfigStripConfigurations,
  CONFIG_STRIP_FEATURE,
  maxUnlimitedQuantityOrder,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import {
  collapsableInventoriesOverviewMinQuantity,
  InventoryOverviewItem,
  isNowDateContainer,
  selectBulkTotalQuantityForSelectedSizeForOrderType,
  selectCurrentlyActiveDeliveryItemsForSelectedSize,
  selectDeliveryQuantityForSelectedSizeForSelectedPeriod,
  selectInventoryOverviewsWithoutBulk,
  selectIsNoInventoriesAvailable,
  selectMaxUnlimitedQuantityOrder,
  selectNotConsumedInventoryOverviews,
  selectPrioritizeMaxUnlimitedQuantityShown,
  selectShowInventoryExternalIdAsCustomLabel,
  selectSplitInventoriesOverviewForSameDateEnabled,
  spreadSelectedQuantityOverInventories,
  unpackNowDate,
} from '@epicuro-next/state/config-strip-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { Severity } from 'graphql-api/baskets-api-interfaces';
import { combineLatest, Subject } from 'rxjs';
import {
  filter,
  map,
  shareReplay,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { isInventoryVisible } from './is-inventory-visible';
import { getMaxShownTextOrNumber } from './max-shown-text';

type InventoryOverviewViewItem = InventoryOverviewItem & {
  maxShownQuantity: string | number;
};

type ViewModel = {
  showInventoryExternalIdAsCustomLabel: boolean;
  showNotConsumableInventory: boolean;
  maxShownBulkQuantity: number | string;
  availableBulk: number;
  maxUnlimitedQuantityOrder: number;
  collapsibleInventoryOverview: boolean;
  availableInventoriesWithoutBulk: InventoryOverviewViewItem[];
  quantitiesPerAvailableInventory: number[];
};

@Component({
  selector: 'epc-inventory-overview',
  templateUrl: './inventory-overview.component.html',
  styleUrls: ['./inventory-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InventoryOverviewComponent implements OnDestroy {
  public maxVisibleInventories = 0;
  public showMore = false;
  public readonly inventoriesLabels = inventoryByExternalIdLocale;
  public readonly nonConsumableLabel = $localize`:Config Strip|Inventory overview non consumable tooltip label@@inventoryNonConsumableLabel:Non consumable`;

  private defaultVisibleInventories = 0;

  @Input() public showShowMore = true;

  // if this number is greater than 0, visible inventories will be limited
  @Input()
  public set limitVisibleInventories(value: number) {
    this.defaultVisibleInventories = value;
    this.maxVisibleInventories = value;
  }

  public isInventoryVisible = isInventoryVisible;
  public unpackNowDate = unpackNowDate;
  public min = Math.min;

  private colorIndexBase = 0;

  /**
   * Currently active period, none is selected initially
   */
  public activePeriod = 0;

  public bulkColor = ProgressBarColor.BULK_CUSTOM;
  public readonly severity = Severity;

  private destroy$ = new Subject<void>();

  public bulkLabel = $localize`:Size Grid|Bulk label@@bulkIndicatorLabel:Bulk`;

  public quantityForSelectedSize$ = this.store.pipe(
    select(selectDeliveryQuantityForSelectedSizeForSelectedPeriod),
  );

  public availableBulk$ = this.store.select(
    selectBulkTotalQuantityForSelectedSizeForOrderType,
  );

  private readonly splitInventoriesOverviewForSameDateEnabled$ =
    this.store.pipe(
      select(selectSplitInventoriesOverviewForSameDateEnabled),
      map(Boolean),
    );

  private readonly showInventoryExternalIdAsCustomLabel$ = this.store.pipe(
    select(selectShowInventoryExternalIdAsCustomLabel),
    map(Boolean),
  );

  private readonly maxUnlimitedQuantityOrder$ = this.store.pipe(
    select(selectMaxUnlimitedQuantityOrder),
    filter(isNonNullish),
  );

  private readonly prioritizeMaxUnlimitedQuantityShown$ = this.store.select(
    selectPrioritizeMaxUnlimitedQuantityShown,
  );

  private readonly inventoriesOverviewMaxShownInventories$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ConfigStripConfigurationKeys.InventoriesOverviewMaxShownInventories,
        ConfigStripConfigurations
      >(
        CONFIG_STRIP_FEATURE,
        ConfigStripConfigurationKeys.InventoriesOverviewMaxShownInventories,
        maxUnlimitedQuantityOrder,
      ),
    ),
    filter(isNonNullish),
  );

  private readonly collapsableInventoryMinQuantity: Signal<number> =
    this.store.selectSignal(collapsableInventoriesOverviewMinQuantity);

  private readonly maxShownBulkQuantity$ = this.availableBulk$.pipe(
    withLatestFrom(this.inventoriesOverviewMaxShownInventories$),
    map(([bulkQuantities, maxShownBulkQuantities]) =>
      getMaxShownTextOrNumber(bulkQuantities, maxShownBulkQuantities),
    ),
  );

  public readonly notConsumedInventoryOverviews$ = this.store.pipe(
    select(selectNotConsumedInventoryOverviews),
    map((inventories) => {
      return inventories.map((i) => {
        return {
          ...i,
          maxShownQuantity: i.maxQuantity,
        };
      });
    }),
  );

  public readonly quantityForSelectedSizeForPeriodForShop = toSignal(
    this.store.pipe(select(selectCurrentlyActiveDeliveryItemsForSelectedSize)),
    {
      initialValue: {
        quantity: 0,
        usedTrueDemandQuantity: 0,
      },
    },
  );

  public readonly usedTrueDemand = computed(
    () => this.quantityForSelectedSizeForPeriodForShop().usedTrueDemandQuantity,
  );

  public readonly suppliedQuantity = computed(
    () =>
      this.quantityForSelectedSizeForPeriodForShop().quantity -
      (this.usedTrueDemand() ?? 0),
  );

  @HostBinding('class.full-width')
  public get usedTrueDemandQuantity() {
    return (this.usedTrueDemand() ?? 0) > 0;
  }

  public readonly availableInventoriesWithoutBulk$ = this.store.pipe(
    select(selectInventoryOverviewsWithoutBulk),
    withLatestFrom(
      this.inventoriesOverviewMaxShownInventories$,
      this.maxUnlimitedQuantityOrder$,
      this.prioritizeMaxUnlimitedQuantityShown$,
    ),
    map(
      ([
        inventories,
        maxShownQuantity,
        maxUnlimitedQuantityToOrder,
        prioritizeMaxUnlimitedQuantityShown,
      ]) => {
        return inventories.map((inventory) => ({
          ...inventory,
          maxShownQuantity: getMaxShownTextOrNumber(
            inventory.maxQuantity,
            maxShownQuantity,
            maxUnlimitedQuantityToOrder,
            prioritizeMaxUnlimitedQuantityShown,
          ),
        }));
      },
    ),
    tap((availableInventories) => {
      if (
        availableInventories.some((availableInventory) =>
          isNowDateContainer(availableInventory.deliveryPeriod),
        )
      ) {
        this.colorIndexBase = 1;
        return;
      }

      this.colorIndexBase = 0;
    }),
    takeUntil(this.destroy$),
  );

  public readonly collapsibleInventoryOverview$ = combineLatest([
    this.store.select(
      selectConfigurationValueFrom<
        ConfigStripConfigurationKeys.CollapsibleInventoryOverview,
        ConfigStripConfigurations
      >(
        CONFIG_STRIP_FEATURE,
        ConfigStripConfigurationKeys.CollapsibleInventoryOverview,
        true,
      ),
    ),
    atxIsMobile$,
    this.availableInventoriesWithoutBulk$,
  ]).pipe(
    map(([enabled, isMobile, availableInventories]) => {
      return Boolean(
        enabled &&
          !isMobile &&
          availableInventories?.length > this.collapsableInventoryMinQuantity(),
      );
    }),
    tap((show) => {
      if (show && this.defaultVisibleInventories !== 0) {
        this.maxVisibleInventories = this.collapsableInventoryMinQuantity();
        this.showMore = false;
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntil(this.destroy$),
  );

  public isNoAvailableInventories$ = this.store.select(
    selectIsNoInventoriesAvailable,
  );

  public consumedBulk$ = combineLatest([
    this.quantityForSelectedSize$,
    this.availableBulk$,
  ]).pipe(map((values) => Math.min(...values)));

  /**
   * Observable for getting the currently entered quantities for the shown availability periods
   */
  public quantitiesPerAvailableInventory$ = combineLatest([
    this.quantityForSelectedSize$,
    this.availableBulk$,
    this.availableInventoriesWithoutBulk$,
  ]).pipe(
    map(([quantity, availableBulk, availableInventories]) =>
      spreadSelectedQuantityOverInventories(
        quantity,
        availableBulk,
        availableInventories,
      ),
    ),
  );

  public readonly vm$ = combineLatest([
    this.splitInventoriesOverviewForSameDateEnabled$,
    this.maxShownBulkQuantity$,
    this.availableBulk$,
    this.maxUnlimitedQuantityOrder$,
    this.collapsibleInventoryOverview$,
    this.availableInventoriesWithoutBulk$,
    this.quantitiesPerAvailableInventory$,
    this.showInventoryExternalIdAsCustomLabel$,
  ]).pipe(
    map(
      ([
        splitInventoriesOverviewEnabled,
        maxShownBulkQuantity,
        availableBulk,
        maxUnlimitedQuantity,
        collapsibleInventoryOverview,
        availableInventoriesWithoutBulk,
        quantitiesPerAvailableInventory,
        showInventoryExternalIdAsCustomLabel,
      ]): ViewModel => {
        return {
          // switch to separate rule in future if needed
          showNotConsumableInventory: splitInventoriesOverviewEnabled,
          maxShownBulkQuantity,
          availableBulk,
          maxUnlimitedQuantityOrder: maxUnlimitedQuantity,
          collapsibleInventoryOverview,
          availableInventoriesWithoutBulk,
          quantitiesPerAvailableInventory,
          showInventoryExternalIdAsCustomLabel,
        };
      },
    ),
  );

  public progressBarColor(deliveryPeriod: string, index: number) {
    if (isNowDateContainer(deliveryPeriod)) {
      return 'back-order-color-now';
    }

    return `back-order-color-${index - this.colorIndexBase}`;
  }

  public showMoreToggle() {
    this.showMore = !this.showMore;
    this.maxVisibleInventories = this.showMore
      ? this.defaultVisibleInventories
      : this.collapsableInventoryMinQuantity();
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor(private store: Store) {}
}
